// Single source of truth for the CADtomie Intraop brand identity *inside
// the Intraop app*. Geometry mirrors `shared/brand/Glyph.tsx` so report
// exports (PDF / PNG, painted via canvas) and on-screen UI use the same
// compass / divider mark.
//
// Note: the React component here exists for backwards compatibility with
// existing Intraop pages. New code should reach for `@shared/brand`
// directly — both produce identical pixels.

import { CSSProperties } from 'react';

export const BRAND_NAME = 'CADtomie Intraop';
export const BRAND_VERSION = '1.0';

/** Module-specific accent colours. */
export const MODULE_COLOR = {
  angle: '#7cc4ff',
  height: '#30d158',
  torsion: '#ff9f0a',
} as const;

export type ModuleKey = keyof typeof MODULE_COLOR;

export const MODULE_TITLE: Record<ModuleKey, string> = {
  angle: 'Osteotomy Angle',
  height: 'Osteotomy Height',
  torsion: 'Torsion',
};

/** Footer disclaimer that ships with every exported report. */
export const DISCLAIMER =
  'Research assistance only · Not a medical device · Verify with established methods';

/* ════════════════ Logo (canvas) ════════════════ */

/**
 * Draws the CADtomie compass / divider glyph into a 2D canvas.
 *
 *   (cx, cy)       baseline-top-left anchor (top-left of bounding box).
 *   size           glyph height in pixels — width is the same.
 *   color          stroke colour (typically the module accent or white).
 *
 * Geometry mirrors the SVG `Glyph` component in `@shared/brand/Glyph.tsx`
 * so on-screen and exported brand renders look identical. Canvas-side
 * stays single-colour (no gradient) — Apple PDF preview and some print
 * pipelines render gradient stops inconsistently, and a solid stroke
 * exports more reliably.
 */
export function drawBrandGlyph(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  size: number,
  color: string,
) {
  // Geometry mirrors shared/brand/Glyph.tsx:
  //   apexX  = 0.50, apexY  = 0.20
  //   leftX  = 0.27, rightX = 0.73
  //   baseY  = 0.86
  //   strokeWidth = 0.13 of size (clamped at 2)
  //   dotRadius   = strokeWidth * 0.88
  const apexX = cx + size * 0.5;
  const apexY = cy + size * 0.2;
  const leftX = cx + size * 0.27;
  const rightX = cx + size * 0.73;
  const baseY = cy + size * 0.86;
  const stroke = Math.max(2, size * 0.13);

  ctx.save();
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.strokeStyle = color;
  ctx.lineWidth = stroke;
  ctx.beginPath();
  ctx.moveTo(leftX, baseY);
  ctx.lineTo(apexX, apexY);
  ctx.lineTo(rightX, baseY);
  ctx.stroke();

  // Apex pivot dot
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.arc(apexX, apexY, stroke * 0.88, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

/* ════════════════ Logo (React) ════════════════ */

interface BrandGlyphProps {
  size?: number;
  color?: string;
  style?: CSSProperties;
}

/**
 * Compass / divider glyph as React/SVG. Geometry mirrors
 * `drawBrandGlyph` above. Kept here (not just imported from
 * `@shared/brand`) because some Intraop modules expect a solid-stroke
 * variant via the `color` prop — the shared version defaults to a
 * gradient. Both APIs are otherwise identical.
 */
export function BrandGlyph({
  size = 32,
  color = MODULE_COLOR.angle,
  style,
}: BrandGlyphProps) {
  const strokeWidth = Math.max(2, size * 0.13);
  const apexX = size * 0.5;
  const apexY = size * 0.2;
  const leftX = size * 0.27;
  const rightX = size * 0.73;
  const baseY = size * 0.86;
  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      style={{ display: 'block', ...style }}
      aria-hidden="true"
    >
      <line
        x1={leftX}
        y1={baseY}
        x2={apexX}
        y2={apexY}
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
      />
      <line
        x1={rightX}
        y1={baseY}
        x2={apexX}
        y2={apexY}
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
      />
      <circle cx={apexX} cy={apexY} r={strokeWidth * 0.88} fill={color} />
    </svg>
  );
}
